/**
 * useABTest.js
 *
 * Deterministic A/B variant assignment based on the persistent visitor_id.
 * Same visitor always gets the same variant across page loads and sessions.
 * Assignments are stored in localStorage under '_ab_variants'.
 *
 * Usage:
 *   const { variant } = useABTest('landing_v1')
 *   // variant === 'control' | 'treatment'
 *
 * Reading all assignments (for tracking payloads):
 *   import { getABVariants } from '../hooks/useABTest'
 *   const abVariants = getABVariants() // { landing_v1: 'control', ... }
 */

import { useState } from 'react';
import { getOrCreateVisitorId } from './useLandingTracking';

const AB_VARIANTS_KEY = '_ab_variants';

// djb2-style hash — fast, deterministic, good distribution
const djb2Hash = (str) => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) + str.charCodeAt(i);
    hash = hash & 0x7fffffff; // keep positive 31-bit int
  }
  return hash;
};

/**
 * Read all stored AB variant assignments.
 * Import this in tracking utilities to attach variant info to event payloads.
 */
export const getABVariants = () => {
  try {
    return JSON.parse(localStorage.getItem(AB_VARIANTS_KEY) || '{}');
  } catch {
    return {};
  }
};

/**
 * @param {string} experimentId  Unique experiment key, e.g. 'landing_v1'
 * @param {Object} options
 * @param {string[]} options.variants  Variant names. Default: ['control', 'treatment']
 * @param {number[]} options.weights   Traffic split (must sum to 100). Default: [50, 50]
 * @returns {{ variant: string }}
 */
const useABTest = (experimentId, { variants = ['control', 'treatment'], weights = [50, 50] } = {}) => {
  const [variant] = useState(() => {
    // Return existing assignment if already stored
    try {
      const stored = JSON.parse(localStorage.getItem(AB_VARIANTS_KEY) || '{}');
      if (stored[experimentId]) return stored[experimentId];
    } catch {}

    // Assign deterministically: hash(visitor_id + experiment_id) → 0–99 bucket
    const visitorId = getOrCreateVisitorId();
    const bucket = djb2Hash(visitorId + experimentId) % 100;

    let cumulative = 0;
    let assigned = variants[0];
    for (let i = 0; i < variants.length; i++) {
      cumulative += weights[i];
      if (bucket < cumulative) {
        assigned = variants[i];
        break;
      }
    }

    // Persist so the same visitor always gets the same variant
    try {
      const existing = JSON.parse(localStorage.getItem(AB_VARIANTS_KEY) || '{}');
      localStorage.setItem(AB_VARIANTS_KEY, JSON.stringify({ ...existing, [experimentId]: assigned }));
    } catch {}

    return assigned;
  });

  return { variant };
};

export default useABTest;
